import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStudy } from '@/hooks/use-studies';
import { toast } from '@/hooks/use-toast';
import { getReportsForStudy, getStudy } from '@/lib/api';
import { seriesClipLabel } from '@/lib/dicom/seriesLabel';
import { studyBaseName } from '@/lib/download/filenames';
import { canSaveToDisk, pickSaveTarget } from '@/lib/download/saveTarget';
import { formatDate, NUMERIC_DATE } from '@/lib/format';
// Only lightweight, pure helpers are imported statically. The heavy export
// engine (Cornerstone rasterizer + ffmpeg.wasm + fflate) is dynamically
// imported inside handleDownload so it stays out of the worklist bundle.
import { applicableFormats, cineClipsForSeries, cineSeriesFor, DownloadError, estimateExport, isCineSeries, isImageFormat, isVideoFormat, willArchive, } from '@/lib/download/types';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileImage, FileText, Film, ImageIcon, Loader2, Stethoscope, } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const FORMAT_ICON = {
    dicom: Stethoscope,
    jpeg: FileImage,
    png: ImageIcon,
    mp4: Film,
    avi: Film,
    pdf: FileText,
};
/** Series that are selectable for a given export format. */
function selectableSeries(study, format) {
    if (isImageFormat(format)) {
        return study.series.filter((s) => s.images.some((i) => !i.isVideo));
    }
    if (isVideoFormat(format)) {
        return cineSeriesFor(study, study.series.map((s) => s.id));
    }
    // DICOM: every series.
    return study.series;
}
export function DownloadStudyDialog({ studyIds, open, onOpenChange, shareStudy, }) {
    const { t } = useTranslation();
    const single = studyIds.length === 1;
    const { data: fetchedStudy, isLoading: studyLoading } = useStudy(single && open && !shareStudy ? studyIds[0] : undefined);
    const study = shareStudy ?? fetchedStudy;
    const { data: reports = [] } = useQuery({
        queryKey: ['study-reports', studyIds[0]],
        queryFn: () => getReportsForStudy(studyIds[0]),
        enabled: single && open && !shareStudy,
        staleTime: 30_000,
    });
    // The dialog is mounted fresh each time it is opened (the parent renders it
    // conditionally), so initial state is always clean — no reset-on-open effect.
    const [userFormat, setUserFormat] = useState(null);
    const [seriesSel, setSeriesSel] = useState({
        key: null,
        set: new Set(),
    });
    const [reportSel, setReportSel] = useState({
        key: null,
        set: new Set(),
    });
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    // Content-aware format set: for a single study we inspect what it actually
    // contains; for a multi-select we can't introspect cheaply, so offer the
    // whole-study formats (DICOM always, PDF for reports).
    const availableFormats = useMemo(() => {
        if (!single)
            return ['dicom', 'pdf'];
        if (!study)
            return [];
        return applicableFormats(study, reports.length);
    }, [single, study, reports.length]);
    // Default selections to "everything" the first time each dataset resolves.
    // Initializing during render (guarded by a content key) is the React-blessed
    // alternative to a setState-in-effect; it re-renders immediately and bails.
    if (study && seriesSel.key !== study.id) {
        setSeriesSel({ key: study.id, set: new Set(study.series.map((s) => s.id)) });
    }
    const reportsKey = reports.length ? reports.map((r) => r.id).join(',') : 'none';
    if (reportSel.key !== reportsKey) {
        setReportSel({ key: reportsKey, set: new Set(reports.map((r) => r.id)) });
    }
    // Derive the effective format (clamped to what's producible) without state sync.
    const format = userFormat && availableFormats.includes(userFormat)
        ? userFormat
        : (availableFormats[0] ?? 'dicom');
    const selectedSeries = seriesSel.set;
    const selectedReports = reportSel.set;
    const seriesForFormat = useMemo(() => (study ? selectableSeries(study, format) : []), [study, format]);
    const toggleSeries = (id) => {
        setSeriesSel((prev) => {
            const next = new Set(prev.set);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return { key: prev.key, set: next };
        });
    };
    const toggleReport = (id) => {
        setReportSel((prev) => {
            const next = new Set(prev.set);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return { key: prev.key, set: next };
        });
    };
    const selectedSeriesForFormat = seriesForFormat.filter((s) => selectedSeries.has(s.id));
    const canDownload = (() => {
        if (running)
            return false;
        if (availableFormats.length === 0)
            return false;
        if (!single)
            return true;
        if (format === 'pdf')
            return selectedReports.size > 0;
        return selectedSeriesForFormat.length > 0;
    })();
    // Pre-flight size estimate (single-study path — the dialog's real use). Everything is
    // fetched + rendered + zipped in the browser, so a very large selection can exhaust memory.
    const exportEstimate = useMemo(() => single && study
        ? estimateExport([
            {
                study,
                seriesIds: Array.from(selectedSeries),
                reportIds: Array.from(selectedReports),
            },
        ], { format })
        : null, [single, study, selectedSeries, selectedReports, format]);
    const handleCancel = () => {
        abortRef.current?.abort();
    };
    const handleDownload = async () => {
        // Where the archive goes is settled FIRST, before anything is awaited: the
        // browser's save picker (Chrome, Edge) needs the click's user gesture, and
        // the archive then streams to that file as it downloads, in bounded
        // memory. One file saves as before, through the download manager.
        const singleSelection = single && study
            ? { study, seriesIds: Array.from(selectedSeries), reportIds: Array.from(selectedReports) }
            : null;
        const archive = singleSelection ? willArchive([singleSelection], { format }) : !single;
        let target = { kind: 'blob' };
        if (archive) {
            const suggested = `${singleSelection ? studyBaseName(singleSelection.study) : 'studies_export'}_${format}.zip`;
            const picked = await pickSaveTarget(suggested, 'application/zip', 'zip');
            if (!picked)
                return; // the reader cancelled the save dialog
            target = picked;
        }
        setRunning(true);
        setError(null);
        setProgress({ phase: 'preparing', completed: 0, total: 0 });
        const controller = new AbortController();
        abortRef.current = controller;
        const reportStatusById = new Map();
        try {
            let selections;
            if (singleSelection) {
                reports.forEach((r) => reportStatusById.set(r.id, r.status));
                selections = [singleSelection];
            }
            else {
                selections = [];
                for (const id of studyIds) {
                    if (controller.signal.aborted)
                        throw new DOMException('Aborted', 'AbortError');
                    // react-doctor-disable-next-line react-doctor/async-await-in-loop -- independent reads, but they precede an export that dominates the wait; kept in order rather than bursting the API
                    const fetched = await getStudy(id);
                    let reportIds = [];
                    if (format === 'pdf') {
                        const studyReports = await getReportsForStudy(id);
                        studyReports.forEach((r) => reportStatusById.set(r.id, r.status));
                        reportIds = studyReports.map((r) => r.id);
                    }
                    selections.push({
                        study: fetched,
                        seriesIds: fetched.series.map((s) => s.id),
                        reportIds,
                    });
                }
            }
            // Lazy-load the heavy export engine only when the user actually downloads.
            const { runDownload } = await import('@/lib/download/runDownload');
            await runDownload(selections, reportStatusById, { format, jpegQuality: 0.92 }, setProgress, controller.signal, target);
            toast({ title: t('download.started') });
            onOpenChange(false);
        }
        catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                setProgress(null);
                return;
            }
            const message = err instanceof DownloadError
                ? t(err.i18nKey, err.i18nParams)
                : err instanceof Error
                    ? err.message
                    : String(err);
            setError(message);
            toast({ title: t('download.failed'), description: message, variant: 'destructive' });
        }
        finally {
            setRunning(false);
            abortRef.current = null;
        }
    };
    const progressPercent = progress && progress.total > 0
        ? Math.min(100, Math.round((progress.completed / progress.total) * 100))
        : null;
    return (_jsx(Dialog, { open: open, onOpenChange: (o) => !running && onOpenChange(o), children: _jsxs(DialogContent, { className: "max-h-[85dvh] overflow-y-auto sm:max-w-lg", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: t('download.title') }), _jsx(DialogDescription, { children: single
                                ? t('download.descriptionSingle')
                                : t('download.descriptionMulti', { count: studyIds.length }) })] }), availableFormats.length > 0 && (_jsx(Tabs, { value: format, onValueChange: (v) => setUserFormat(v), children: _jsx(TabsList, { className: "grid w-full", style: { gridTemplateColumns: `repeat(${availableFormats.length}, 1fr)` }, children: availableFormats.map((f) => {
                            const Icon = FORMAT_ICON[f];
                            return (_jsxs(TabsTrigger, { value: f, disabled: running, children: [_jsx(Icon, { className: "mr-1.5 h-4 w-4" }), t(`download.format.${f}`)] }, f));
                        }) }) })), !single ? (_jsx("p", { className: "text-muted-foreground text-sm", children: t('download.multiNote', { count: studyIds.length }) })) : studyLoading ? (_jsx("div", { className: "flex justify-center py-6", children: _jsx(Loader2, { className: "text-muted-foreground h-5 w-5 animate-spin" }) })) : format === 'pdf' ? (_jsx(ReportPicker, { reports: reports, selected: selectedReports, onToggle: toggleReport, disabled: running })) : (_jsx(SeriesPicker, { series: seriesForFormat, selected: selectedSeries, onToggle: toggleSeries, format: format, disabled: running })), error && _jsx("p", { className: "text-destructive text-sm", children: error }), running && progress && (_jsxs("div", { className: "space-y-1", children: [_jsx("div", { className: "bg-muted h-2 w-full overflow-hidden rounded-full", children: _jsx("div", { className: "bg-primary h-full transition-all", style: { width: progressPercent !== null ? `${progressPercent}%` : '100%' } }) }), _jsxs("p", { className: "text-muted-foreground text-xs", children: [t(`download.phase.${progress.phase}`), progress.total > 0 ? ` · ${progress.completed}/${progress.total}` : '', progress.detail ? ` · ${progress.detail}` : ''] })] })), !running && exportEstimate?.isLarge && (_jsxs("div", { className: "flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2.5", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0 text-amber-500" }), _jsx("p", { className: "text-muted-foreground text-xs leading-relaxed", children: t(canSaveToDisk() ? 'download.diskNote' : 'download.largeWarning', {
                                count: exportEstimate.imageCount,
                                series: exportEstimate.seriesCount,
                            }) })] })), _jsxs("div", { className: "flex justify-end gap-2", children: [running ? (_jsx(Button, { variant: "secondary", onClick: handleCancel, children: t('download.cancel') })) : (_jsx(Button, { variant: "ghost", onClick: () => onOpenChange(false), children: t('download.close') })), _jsxs(Button, { onClick: handleDownload, disabled: !canDownload, children: [running ? _jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }) : null, t('download.download')] })] })] }) }));
}
function SeriesPicker({ series, selected, onToggle, format, disabled, }) {
    const { t } = useTranslation();
    if (series.length === 0) {
        return (_jsx("p", { className: "text-muted-foreground py-4 text-center text-sm", children: t('download.noSeries') }));
    }
    const countsFrames = isImageFormat(format);
    return (_jsx(ScrollArea, { className: "max-h-64", children: _jsx("div", { className: "space-y-1 pr-2", children: series.map((s) => {
                const imageCount = s.images.length;
                const cine = isCineSeries(s);
                return (_jsxs("label", { className: "hover:bg-muted/40 flex cursor-pointer items-center gap-3 rounded px-2 py-1.5", children: [_jsx(Checkbox, { checked: selected.has(s.id), disabled: disabled, onCheckedChange: () => onToggle(s.id) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm", children: [_jsx("span", { className: "font-medium", children: seriesClipLabel(s, t) ??
                                                t('download.seriesLabel', { num: s.seriesNumber ?? '?' }) }), s.modality && (_jsx("span", { className: "bg-muted rounded px-1.5 py-0.5 text-[10px] font-medium", children: s.modality })), cine && (_jsx("span", { className: "rounded bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-400", children: t('download.cine') }))] }), _jsxs("div", { className: "text-muted-foreground truncate text-xs", children: [s.seriesDescription || s.modality || '—', " \u00B7", ' ', countsFrames
                                            ? t('download.frameCount', {
                                                count: s.images.reduce((n, i) => n + Math.max(1, i.numberOfFrames ?? 1), 0),
                                            })
                                            : isVideoFormat(format)
                                                ? // Each loop is its own file, so say how many files the series makes.
                                                    t('download.clipCount', { count: cineClipsForSeries(s).length })
                                                : t('download.imageCount', { count: imageCount })] })] })] }, s.id));
            }) }) }));
}
function ReportPicker({ reports, selected, onToggle, disabled, }) {
    const { t } = useTranslation();
    if (reports.length === 0) {
        return (_jsx("p", { className: "text-muted-foreground py-4 text-center text-sm", children: t('download.noReports') }));
    }
    return (_jsx("div", { className: "space-y-1", children: reports.map((r) => (
        // One line is 32px; on touch each row is 44px, the pitch of the
        // checkbox's hit area, so a row's hit area never takes a tap meant for
        // the report above it. (The two-line series rows are already taller.)
        _jsxs("label", { className: "hover:bg-muted/40 flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 text-sm pointer-coarse:min-h-11", children: [_jsx(Checkbox, { checked: selected.has(r.id), disabled: disabled, onCheckedChange: () => onToggle(r.id) }), _jsx("span", { className: "font-medium uppercase", children: r.status }), _jsx("span", { className: "text-muted-foreground text-xs", children: formatDate(r.updatedAt, NUMERIC_DATE) })] }, r.id))) }));
}
